import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Download } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { jourLisible } from '../veille/format';
import { ErreurApi } from '../../lib/api';
import {
  telechargerDossierAudit,
  useApercuDossierAudit,
  type ChoixPeriodeAudit,
  type DemandeDossierAudit,
  type FormatDossierAudit,
  type IndicateurQualiopi,
  type ReferentielDossierAudit,
} from '../../lib/useQualiopi';

const LEGENDE = 'mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray';
const CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none';

function poids(octets: number): string {
  if (octets === 0) return '0 Mo';
  if (octets < 1024 * 1024) return `${Math.max(1, Math.round(octets / 1024)).toLocaleString('fr-FR')} Ko`;
  return `${(octets / 1024 / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre.toLocaleString('fr-FR')} ${nombre > 1 ? plurielTexte : singulier}`;
}

function messageDe(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/** Case d'un critère : cochée quand tous ses indicateurs le sont, à moitié quand certains seulement. */
function CaseCritere({
  coches,
  total,
  onChange,
  children,
}: {
  coches: number;
  total: number;
  onChange: (tout: boolean) => void;
  children: ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = coches > 0 && coches < total;
  }, [coches, total]);
  return (
    <label className="flex min-h-[36px] min-w-0 cursor-pointer items-center gap-2 text-sm font-semibold text-codialis-navy [@media(pointer:coarse)]:min-h-[44px]">
      <input
        ref={ref}
        type="checkbox"
        className="shrink-0 rounded"
        checked={coches === total}
        onChange={() => onChange(coches < total)}
      />
      <span className="min-w-0">{children}</span>
    </label>
  );
}

/**
 * Dossier d'audit Qualiopi (TF-432) : le rapport de conformité et ses pièces,
 * en archive ZIP rangée par indicateur ou en PDF unique, pour une période et
 * des indicateurs choisis. L'aperçu — pièces, liens, poids, refus — vient de
 * l'API (`domain/export-audit-qualiopi.ts`) ; l'écran ne compte rien.
 */
export function ModaleDossierAudit({
  referentiel,
  criteres,
  site,
  critereInitial,
  onFermer,
}: {
  referentiel: ReferentielDossierAudit;
  criteres: { numero: number; titre: string; indicateurs: IndicateurQualiopi[] }[];
  site: string;
  /** Critère filtré à l'écran : ses indicateurs sont cochés d'office. */
  critereInitial: number | null;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const tous = criteres.flatMap((critere) => critere.indicateurs.map((indicateur) => indicateur.numero));
  const [format, setFormat] = useState<FormatDossierAudit>(referentiel.formats[0]?.valeur ?? 'ZIP');
  const [periode, setPeriode] = useState<ChoixPeriodeAudit>(referentiel.periodeParDefaut);
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [choisis, setChoisis] = useState<Set<number>>(() => {
    const duCritere = criteres.find((critere) => critere.numero === critereInitial);
    return new Set(duCritere ? duCritere.indicateurs.map((indicateur) => indicateur.numero) : tous);
  });
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const demande: DemandeDossierAudit = {
    format,
    periode,
    ...(periode === 'LIBRE' ? { du, au } : {}),
    indicateurs: choisis.size === tous.length ? [] : [...choisis].sort((a, b) => a - b),
    site,
  };
  const libreIncomplete = periode === 'LIBRE' && (!du || !au);
  const aucun = choisis.size === 0;
  const { data: apercu, error: erreurApercu, isFetching } = useApercuDossierAudit(demande, !libreIncomplete && !aucun);

  const refus = aucun
    ? 'Cochez au moins un indicateur.'
    : libreIncomplete
      ? 'Indiquez le premier et le dernier jour de la période.'
      : erreurApercu
        ? messageDe(erreurApercu, 'L’aperçu du dossier n’a pas pu être calculé.')
        : (apercu?.refus ?? null);

  const changer = (suite: () => void): void => {
    setErreur(null);
    suite();
  };
  const basculer = (numeros: number[], cocher: boolean): void =>
    changer(() =>
      setChoisis((avant) => {
        const suivants = new Set(avant);
        for (const numero of numeros) {
          if (cocher) suivants.add(numero);
          else suivants.delete(numero);
        }
        return suivants;
      }),
    );

  const telecharger = async (): Promise<void> => {
    if (refus || !apercu) return;
    setEnCours(true);
    setErreur(null);
    try {
      const { nomFichier } = await telechargerDossierAudit(demande);
      toast(`Dossier d’audit téléchargé — ${nomFichier}`);
      onFermer();
    } catch (refusApi) {
      setErreur(messageDe(refusApi, 'Le dossier d’audit n’a pas pu être produit.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      ouverte
      titre="Dossier d’audit"
      sousTitre="Le rapport de conformité et les pièces qui le prouvent, pour l’auditeur"
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-5 p-4 sm:p-6">
        <fieldset>
          <legend className={LEGENDE}>Format</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {referentiel.formats.map((un) => {
              const actif = un.valeur === format;
              return (
                <label
                  key={un.valeur}
                  className={`flex min-w-0 cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                    actif ? 'border-codialis-navy bg-codialis-sand' : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="format-dossier-audit"
                    className="mt-0.5 shrink-0"
                    checked={actif}
                    onChange={() => changer(() => setFormat(un.valeur))}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-codialis-navy">{un.libelle}</span>
                    <span className="block text-[11px] text-codialis-gray">{un.detail}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset>
          <legend className={LEGENDE}>Période</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="min-w-0">
              <span className="sr-only">Période</span>
              <select
                className={CHAMP}
                value={periode}
                onChange={(evenement) => changer(() => setPeriode(evenement.target.value as ChoixPeriodeAudit))}
              >
                {referentiel.periodes.map((une) => (
                  <option key={une.valeur} value={une.valeur}>
                    {une.libelle}
                  </option>
                ))}
              </select>
            </label>
            {periode === 'LIBRE' && (
              <>
                <label className="min-w-0">
                  <span className="mb-1 block text-[11px] text-codialis-gray">Du</span>
                  <input
                    type="date"
                    className={CHAMP}
                    value={du}
                    onChange={(evenement) => changer(() => setDu(evenement.target.value))}
                  />
                </label>
                <label className="min-w-0">
                  <span className="mb-1 block text-[11px] text-codialis-gray">Au</span>
                  <input
                    type="date"
                    className={CHAMP}
                    value={au}
                    onChange={(evenement) => changer(() => setAu(evenement.target.value))}
                  />
                </label>
              </>
            )}
          </div>
          <p className="mt-2 text-[11px] text-codialis-gray">
            La période borne les comptes d’activité des contrôles et les preuves retenues : celles qui étaient valables
            pendant la période et n’ont pas été retirées.
          </p>
        </fieldset>

        <fieldset>
          <legend className={LEGENDE}>
            Indicateurs · {choisis.size} sur {tous.length}
          </legend>
          <div className="mb-1 flex flex-wrap gap-x-3">
            <button
              type="button"
              onClick={() => basculer(tous, true)}
              className="min-h-[32px] text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
            >
              Tout cocher
            </button>
            <button
              type="button"
              onClick={() => basculer(tous, false)}
              className="min-h-[32px] text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
            >
              Tout décocher
            </button>
          </div>
          <div className="max-h-[40vh] space-y-3 overflow-y-auto rounded-xl border border-gray-100 p-3">
            {criteres.map((critere) => {
              const numeros = critere.indicateurs.map((indicateur) => indicateur.numero);
              const coches = numeros.filter((numero) => choisis.has(numero)).length;
              return (
                <div key={critere.numero}>
                  <CaseCritere coches={coches} total={numeros.length} onChange={(tout) => basculer(numeros, tout)}>
                    Critère {critere.numero} · {critere.titre}
                  </CaseCritere>
                  <div className="grid grid-cols-1 gap-x-4 pl-6 sm:grid-cols-2">
                    {critere.indicateurs.map((indicateur) => (
                      <label
                        key={indicateur.numero}
                        className="flex min-h-[32px] min-w-0 cursor-pointer items-center gap-2 text-xs text-codialis-navy [@media(pointer:coarse)]:min-h-[44px]"
                      >
                        <input
                          type="checkbox"
                          className="shrink-0 rounded"
                          checked={choisis.has(indicateur.numero)}
                          onChange={() => basculer([indicateur.numero], !choisis.has(indicateur.numero))}
                        />
                        <span className="min-w-0">
                          <span className="font-semibold">{indicateur.numero}</span> · {indicateur.titre}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </fieldset>

        <div aria-live="polite" className="space-y-1 rounded-xl bg-gray-50 p-3 text-[11px] text-codialis-gray">
          {apercu && !refus ? (
            <>
              <p>
                <span className="font-semibold text-codialis-navy">
                  {pluriel(apercu.pieces, 'pièce')} sur {apercu.indicateursLibelle}
                </span>{' '}
                · {pluriel(apercu.fichiers, 'fichier')}, {pluriel(apercu.liens, 'lien')}
                {isFetching ? ' · mise à jour…' : ''}
              </p>
              <p>
                Période {apercu.periode.choix === 'LIBRE' ? '' : `${apercu.periode.libelle}, `}
                du {jourLisible(apercu.periode.du)} au {jourLisible(apercu.periode.arreteeAu)}
                {apercu.periode.arreteeAu !== apercu.periode.au ? ' (aujourd’hui)' : ''}.
              </p>
              <p>
                {format === 'ZIP'
                  ? `${poids(apercu.octets)} de pièces jointes, rangées sous chaque indicateur auquel elles répondent.`
                  : `${pluriel(apercu.fusionnees, 'preuve PDF ou image jointe', 'preuves PDF ou images jointes')} à la suite du rapport${
                      apercu.listees > 0
                        ? `, ${pluriel(apercu.listees, 'document bureautique seulement listé', 'documents bureautiques seulement listés')} (joints à l’archive ZIP)`
                        : ''
                    }.`}
              </p>
            </>
          ) : (
            <p>{isFetching ? 'Calcul de l’aperçu…' : 'Choisissez la période et les indicateurs.'}</p>
          )}
          <p>
            Les pièces nominatives — CV des formateurs, feuilles d’émargement, dossiers des apprenants — n’y figurent
            pas : elles se consultent dans la plateforme, sur l’échantillon que l’auditeur choisit.
          </p>
        </div>

        {(refus ?? erreur) && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {refus ?? erreur}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={() => void telecharger()} disabled={enCours || refus !== null || !apercu}>
            <Download className="mr-1.5 h-4 w-4" aria-hidden />
            {enCours ? 'Préparation du dossier…' : 'Télécharger le dossier'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}
